/* eslint-disable @typescript-eslint/ban-ts-comment */

/**
 * ModuleName: store/upload
 * Description: 上传任务本地管理
 *
 * @author : lushaoqing
 * @date : 2023/05/15 17:00
 */

import { defineStore } from 'pinia'
import { reactive, ref, nextTick, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import { pathJoin, getFileIconUrl } from '@remote/utils/file-helper'
import { fileExit } from '@remote/api/adapter/shell-mqtt'
import { store } from '@remote/store'
import global from '@remote/store/constantStore'
import { logFactory } from '@remote/utils/logger'
import getUploaderInstance, { UploaderTaskManager, LARGE_FILE_SIZE } from './uploader'
import Events from './uploader/enum'
// import { setCacheUploadCompleted, getCacheUploadCompleted } from './helper'
import iconLink from '@remote/assets/img/magnet.png'
import { ITransfeTaskItem } from '@remote/type/upload'
import { toast } from '@remote/utils/dialog'
import { safeJSONParse } from '@shared/utils'

const log = logFactory('Upload-Store')
export interface IInputFileEvent extends Event {
  target: HTMLInputElement
}
type TPhase = 'PHASE_TYPE_PENDING' | 'PHASE_TYPE_RUNNING' | 'PHASE_TYPE_ERROR' | 'PHASE_TYPE_COMPLETE' | 'PHASE_TYPE_PAUSE'
type TLocalTash = {
  kind: string
  id: string
  type: 'upload'
  params: Partial<{
    url: string
    file_id: string
    spec: string
    error: string
    speed: number
  }>
  file_id: string
  file_name: string
  file_size: string
  phase: TPhase
  upProgress: number
  progress: number
  icon_link: string
  path: string
  status: number
  updated_time: number
  upLoadlimit: boolean
  forcePause: boolean
  uploadBy: 'http' | 'oss'
  extData: {dir?:string; name?:string}
}
type TAction = 'pause' | 'restart' | 'delete' | 'recreate' | 'finish'

export const LOCAL_STATUS_SUBMITING = -4
export const LOCAL_STATUS_PARSE_FAIL = -1
export const createTask = (id: string, taskInfo?: Partial<TLocalTash>): TLocalTash => {
  return {
    'kind': 'drive/task',
    'id': id,
    'type': 'upload',
    'params': {
      'url': id,
      'file_id': id,
      'spec': 'pause',
      'error': '',
      speed: 0,
    },
    'file_id': '',
    'file_size': '0',
    'phase': 'PHASE_TYPE_PENDING',
    'file_name': '',
    upProgress: 0,
    'progress': 0,
    'icon_link': iconLink,
    'status': 3,
    path: '',
    updated_time: Date.now(),
    upLoadlimit: false,
    forcePause: false,
    uploadBy: 'oss',
    extData: {dir: taskInfo?.path, name: taskInfo?.file_name},
    ...taskInfo,
  }
}

export const all = reactive<{ [key: string]: TLocalTash }>({})

export const useUpload = defineStore('upload', () => {
  const router = useRouter()
  const tasksIng = ref<string[]>([])
  const tasksDone = ref<string[]>([])
  const tasks = ref<string[]>([])
  const foldTask = ref<{ ing: boolean; done: boolean }>({ ing: false, done: false })
  const isDifferenceTenPercent = ref({ isClose: false, isLimit: true, isVisible: false })
  const completedNums = ref<number>(0)

  let uploader: UploaderTaskManager

  // 删除
  const pop = (id?: string, isDeleteMap?: boolean, isCompleted?: boolean) => {
    // const targetTasks = isCompleted ? tasksDone : tasksIng
    const targetTasks = tasksIng
    const taskId = id || targetTasks.value.pop()
    if (taskId) {
      const index = targetTasks.value.findIndex(task => task === taskId)
      targetTasks.value.splice(index, 1)
      // if (isCompleted) {
      //     setCacheUploadCompleted(tasksDone.value.slice().map(id => Object.assign({}, all[id])))
      // }
      uploader.stopTask(taskId)
      uploader.removeTask(taskId)
      completedNums.value = completedNums.value + 1
      return nextTick(() => {
        if (all[taskId] && isDeleteMap) delete all[taskId]
      })
    }
    return Promise.resolve()
  }

  // 添加
  const push = (id: string, taskInfo?: Partial<TLocalTash> | null, isCompleted?: boolean) => {
    const insertToTask = () => {
      const targetTasks = isCompleted ? tasksDone : tasksIng
      if (isCompleted) {
        targetTasks.value.unshift(id)
      } else {
        targetTasks.value.unshift(id)
      }
    }

    if (!taskInfo) return insertToTask()
    if (all[id]) {
      pop(id).then(() => {
        const task = createTask(id)
        all[task.id] = task
        insertToTask()
      })
    } else {
      const task = createTask(id, taskInfo)
      all[task.id] = task
      insertToTask()
    }
  }

  // 更新
  const update = (taskId: string, taskInfo: Partial<TLocalTash>, action?: TAction) => {
    log('update', taskId, action)
    const handler = (taskId: string) => {
      const prev = all[taskId]
      taskInfo.forcePause = action === 'pause'
      // 暂停的任务不再更新
      if (!action && prev.forcePause || prev.phase === 'PHASE_TYPE_COMPLETE') return
      // 更新前后内容
      if (prev && taskInfo && (!action || prev.phase !== taskInfo.phase)) {
        // 计算速度
        let speed = 0
        let nextParams = {}
        const fileSize = Number(prev.file_size)

        if (taskInfo.upProgress) {
          // @ts-ignore
          const sectionSize = Math.abs(fileSize * (taskInfo.upProgress - prev.upProgress) / 100)
          const sectionInterval = Math.abs(Number(taskInfo.updated_time) - prev.updated_time)
          speed = (sectionSize / sectionInterval) * 1000 > fileSize / 2 ? (
            (fileSize / 2) * (0.8 + Math.random() / 3)
          ) : (sectionSize / sectionInterval) * 1000
          nextParams = Object.assign({}, prev.params, taskInfo.params, { speed })
        } else {
          nextParams = Object.assign({}, prev.params, taskInfo.params)
        }

        // 计算上传和落盘文件差，自动控制上传至oss进度
        if (
          prev.uploadBy === 'oss' &&
                    isDifferenceTenPercent.value.isLimit &&
                    (taskInfo.upProgress || prev.upProgress) &&
                    (taskInfo.progress || prev.progress)
        ) {
          const upProgress = taskInfo.upProgress || prev.upProgress
          const progress = taskInfo.progress || prev.progress
          const diff = ((upProgress - progress) * fileSize) / fileSize
          if (diff > 10) {
            console.log('落盘相差10%')
            // @ts-ignore
            nextParams.speed = 0
            if (!prev.upLoadlimit) {
              // 展示速度限制条
              isDifferenceTenPercent.value.isVisible = true
              taskInfo.upLoadlimit = true
              uploader.pauseTaskToWaitDeviceReceive(taskId)
            }
          } else if (diff < 5) {
            console.log('落盘相差5%', prev.upLoadlimit)
            if (prev.upLoadlimit) {
              taskInfo.upLoadlimit = false
              uploader.continueTaskToUploadOSS(taskId)
            }
          }
        } else {
          // 解除上传限制
          if (prev.upLoadlimit) {
            taskInfo.upLoadlimit = false
            uploader.continueTaskToUploadOSS(taskId)
          }
        }

        if (!isDifferenceTenPercent.value.isLimit) {
          if (taskInfo.upProgress) {
            taskInfo.progress = Math.max(taskInfo.upProgress || 0, prev.upProgress || 0, prev.progress || 0)
            console.log('progress 1', taskId, taskInfo.progress, taskInfo.upProgress || 0, prev.upProgress || 0, prev.progress || 0)
          } else if (taskInfo.progress) {
            taskInfo.progress = Math.max(taskInfo.progress || 0, prev.upProgress || 0, prev.progress || 0)
            console.log('progress 2', taskId, taskInfo.progress, taskInfo.upProgress || 0, prev.upProgress || 0, prev.progress || 0)
          }
        }

        const next = Object.assign(prev, taskInfo, { params: nextParams })
        all[taskId] = next
      }
    }
    // 全部暂停和全部开始操作
    if (taskId === 'all') {
      console.log('taskInfo 1', taskInfo)
      tasksIng.value.forEach(handler)
      uploader?.batchUpdateTask(action as 'restart' | 'pause')
    } else {
      if (action) {
        switch (action) {
          case 'pause':
            uploader?.stopTask(taskId)
            break
          case 'restart':
            uploader?.starTask(taskId)
            break
          case 'delete':
            uploader?.stopTask(taskId)
            uploader?.removeTask(taskId)
            pop(taskId, true)
            completedNums.value = completedNums.value + 1
            break
        }
      }
      if (action !== 'delete') handler(taskId)
    }
  }

  const handleOnSelectFile = async (root: string, evt: IInputFileEvent) => {
    if (!evt?.target?.files?.length) return
    // 过滤空文件
    const filterEmptyFile = (file: File) => !!file.size
    const files = Array.prototype.filter.call(evt?.target?.files, filterEmptyFile)
    // 校验所选文件是否为空
    if (!files && !files?.length) {
      toast({type: 'error', message: '添加上传失败'})
      return console.info('当前目录下不存在文件')
    }
    // 校验当前上传目录是否存在
    const [err, res] = await fileExit(root)
    if (!err && res?.data) {
      const data = safeJSONParse(res?.data) as {result: {exist:boolean}}
      if (!data?.result?.exist)  return toast({type: 'error', message: '当前上传文件目录不存在'})
    }

    const uploader = getUploaderInstance()
    const willUpLoadTasks: ITransfeTaskItem[] = []
    const createFileTask = (file: File): ITransfeTaskItem => ({
      file: file,
      deviceId: global.fromSpace,
      // @ts-ignore
      path: file.webkitRelativePath ? pathJoin(root, file.webkitRelativePath.replace(file.name, '')) : root,
      attachmentId: uuidv4(),
      status: 'Upload.pending',
    })

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const task = createFileTask(file)
      push(task.attachmentId, {
        file_name: file.name,
        file_size: String(file.size),
        icon_link: getFileIconUrl(file.name),
        path: task.path,
        uploadBy: file.size > LARGE_FILE_SIZE ? 'oss' : 'http',
      })
      willUpLoadTasks.push(task)
    }

    if (willUpLoadTasks.length) uploader?.addTask(willUpLoadTasks)
    toast({
      message: '已添加到传输列表',
      forward: '立即查看',
      onclick: () => {
        store.commit('update', { taskType: 'done' })
        router.push('/')
      },
    })
  }

  const handleTakeOffDiffLimit = () => {
    isDifferenceTenPercent.value.isLimit = false
    // 设置到缓存
  }
  const handleCloseDiffTip = () => {
    isDifferenceTenPercent.value.isClose = true
  }

  const onBatchUpdateTask = (opreate: 'stop' | 'start') => {
    log('onBatchUpdateTask action', opreate)
  }

  const onUploadTaskTransitServerProgress = (md5sum: string, percentage: number) => {
    log('onUploadTaskTransitServerProgress ', md5sum, percentage)
    update(md5sum, {
      status: 3,
      upProgress: percentage * 100,
      phase: 'PHASE_TYPE_RUNNING',
      params: { spec: 'running' },
      updated_time: Date.now(),
    })
  }

  const onUploadTaskDeviceReceiveProgress = (md5sum: string, percentage: number) => {
    log('onUploadTaskDeviceReceiveProgress ', md5sum, percentage)
    // 已开启离线上传且该任务不受设备上传限制则无需更新设备接收进度
    if (!isDifferenceTenPercent.value.isLimit && !all[md5sum].upLoadlimit) return
    update(md5sum, {
      status: 3,
      progress: percentage * 100,
      phase: 'PHASE_TYPE_RUNNING',
      params: { spec: 'running' },
    })
  }

  const onUploadTaskTransitServerCompleted = (md5sum: string) => {
    log('onUploadTaskTransitServerCompleted ', md5sum)
    update(md5sum, {
      status: 3,
      upProgress: 100,
      phase: 'PHASE_TYPE_RUNNING',
      params: { spec: 'running' },
    })
  }

  const onUploadTaskDeviceReceiveCompleted = (md5sum: string) => {
    log('onUploadTaskDeviceReceiveCompleted ', md5sum)
    update(md5sum, { status: 1, progress: 100, phase: 'PHASE_TYPE_COMPLETE', updated_time: Date.now(), params: { spec: 'finish' } })
    completedNums.value = completedNums.value + 1
    // pop(md5sum)
    // push(md5sum, null, true)
    // setCacheUploadCompleted(tasksDone.value.slice().map(id => Object.assign({}, all[id])))
  }

  const onUploadTaskError = (md5sum: string, error: string) => {
    log('onUploadTaskError ', md5sum)
    update(md5sum, { status: 1, phase: 'PHASE_TYPE_ERROR', params: { error: error }, updated_time: Date.now() })
  }
  const onUploadTaskPause = (md5sum: string, percentage: number) => {
    log('onUploadTaskPause ', md5sum)
    update(md5sum, { status: 1, progress: percentage * 100, phase: 'PHASE_TYPE_PENDING', params: { spec: 'pause' }, updated_time: Date.now() })
  }

  const toggleFoldTask = (type: 'ing' | 'done') => {
    foldTask.value[type] = !foldTask.value[type]
  }

  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })

  watch(target, async (val) => {
    if (val) {
      log('注册上传事件监听')
      uploader = getUploaderInstance() as UploaderTaskManager
      uploader.addEventListener(Events.UploadTaskBatchUpdate, onBatchUpdateTask)
      uploader.addEventListener(Events.UploadTaskTransitServerProgress, onUploadTaskTransitServerProgress)
      uploader.addEventListener(Events.UploadTaskDeviceReceiveProgress, onUploadTaskDeviceReceiveProgress)
      // uploader.addEventListener(Events.UploadTaskTransitServerCompleted, onUploadTaskTransitServerCompleted)
      uploader.addEventListener(Events.UploadTaskTransitServerCompleted, onUploadTaskTransitServerCompleted)
      uploader.addEventListener(Events.UploadTaskDeviceReceiveCompleted, onUploadTaskDeviceReceiveCompleted)
      uploader.addEventListener(Events.UploadTaskError, onUploadTaskError)
      uploader.addEventListener(Events.UploadTaskPause, onUploadTaskPause)
    }
  })

  const hasTasking = computed(() => {
    return !tasksIng.value.length || completedNums.value !== tasksIng.value.length
  })

  function handleOnbeforeunload (e) {
    const message = '有上传任务正在进行中，确认将离开当前页面吗？'
    e.preventDefault()
    e.returnValue = message
    return message
  }
  watch(hasTasking, (val) => {
    if (val) {
      console.log('hasTasking', val)
      window.removeEventListener('beforeunload', handleOnbeforeunload)
      window.addEventListener('beforeunload', handleOnbeforeunload)
    } else {
      console.log('hasTasking', val)
      window.removeEventListener('beforeunload', handleOnbeforeunload)
    }
  })

  return {
    tasksIng,
    tasksDone,
    tasks,
    foldTask,
    isDifferenceTenPercent,
    push,
    pop,
    update,
    handleOnSelectFile,
    toggleFoldTask,
    handleTakeOffDiffLimit,
    handleCloseDiffTip,
  }
})